import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiJson, ApiError } from '../api'
import type { ThemeTokens } from '../theme'
import { pageStyles } from '../styles'

/**
 * Settings > Model access mode: the enforce/observe switch for the gateway's
 * model-access guardrail (docs/design/specs/2026-09-25-model-access-mode-
 * switch.md). Follows the Policy gate section's idiom: a control with a real
 * consequence, an impact statement, a confirm.
 *
 * FOUR FACTS THE PAGE MUST NOT BLUR, because blurring them is how a control
 * comes to look like it works:
 *   configured  what terraform set: the fallback, possibly unknown
 *   override    what the live SSM parameter says (absent / set / ignored / unreadable)
 *   effective   what the gateway will use given the two
 *   confirmed   whether the gateway has been SEEN acting on it (the log check)
 *
 * The impact figure is the number the operator decides on, so its limits are
 * printed next to it: the log holds refusals only (allows are debug and are
 * not there), so zero is weak evidence and never a clean bill of health.
 */

type Mode = 'observe' | 'enforce'

type OverrideView =
  | { state: 'absent' }
  | { state: 'set'; mode: Mode; setBy: string; setAt: string }
  | { state: 'ignored'; problem: string }
  | { state: 'error'; error: string }

/** `GET /api/settings/model-access`. */
interface Status {
  parameter: string
  /** null = this console was not told (unknown), never a guess. */
  configured: Mode | 'off' | null
  override: OverrideView
  effective: Mode | 'off' | null
  effectiveBecause: 'guardrail-off' | 'override' | 'configured' | 'unknown'
  canSet: boolean
  cannotSetReason: string | null
  unchanged?: boolean
  auditError?: string | null
}

/** `GET /api/settings/model-access/impact`. */
interface Impact {
  windowHours: number
  since: string
  until: string
  complete: boolean
  incompleteReason: string | null
  pagesRead: number
  lines: number
  notEntitled: {
    people: number
    peopleList: string[]
    byModel: Array<{ model: string; people: number; requests: number }>
  }
  modelsWithNoGrant: Array<{ model: string; requests: number }>
  checkFailed: { people: number; requests: number }
  noIdentity: { requests: number }
  otherRefusals: { requests: number }
  lastLogged: { mode: string; verdict: string; at: string; modeSource?: string | null; modeSetAt?: string | null } | null
  overrideWarnings?: Array<{ kind: 'unreadable' | 'not-used' | 'gate-misconfigured'; count: number; newestAt: string; text: string }>
  modesSeen: string[]
}
type Confirmation = 'no-override' | 'confirmed' | 'contradicted' | 'unconfirmed' | 'gateway-cannot-read'
interface ImpactResponse {
  impact: Impact
  confirmation: Confirmation
}

const SHOWN_PEOPLE = 10

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return `${err.message} (HTTP ${err.status})`
  return err instanceof Error ? err.message : String(err)
}

function when(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('en-AU', { timeZone: 'Australia/Sydney', dateStyle: 'medium', timeStyle: 'short' })
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

export function ModelAccessSwitch({ tokens }: { tokens: ThemeTokens }) {
  const styles = useMemo(() => pageStyles(tokens), [tokens])

  const [status, setStatus] = useState<Status | null>(null)
  const [statusError, setStatusError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [confirming, setConfirming] = useState(false)
  const [reason, setReason] = useState('')

  /** null = not read yet; the confirm and the standalone viewer both use it. */
  const [impact, setImpact] = useState<ImpactResponse | null>(null)
  const [impactError, setImpactError] = useState<string | null>(null)
  const [impactLoading, setImpactLoading] = useState(false)
  const [viewing, setViewing] = useState(false)

  const loadStatus = useCallback(async () => {
    setStatusError(null)
    try {
      setStatus(await apiJson<Status>('/api/settings/model-access'))
    } catch (err) {
      setStatus(null)
      setStatusError(`Could not read the model access mode: ${errorMessage(err)}`)
    }
  }, [])

  useEffect(() => {
    void loadStatus()
  }, [loadStatus])

  /** Fetched fresh every time, never cached across a page sit: a figure a
   *  decision rests on is only good at the moment it is read. */
  const loadImpact = useCallback(async () => {
    setImpactLoading(true)
    setImpactError(null)
    setImpact(null)
    try {
      setImpact(await apiJson<ImpactResponse>('/api/settings/model-access/impact'))
    } catch (err) {
      setImpactError(errorMessage(err))
    } finally {
      setImpactLoading(false)
    }
  }, [])

  function openConfirm() {
    setConfirming(true)
    setViewing(false)
    setReason('')
    setNotice(null)
    setActionError(null)
    void loadImpact()
  }

  function openViewer() {
    setViewing(true)
    setConfirming(false)
    void loadImpact()
  }

  async function put(mode: Mode, extra: Record<string, unknown> = {}) {
    setBusy(true)
    setActionError(null)
    setNotice(null)
    try {
      const next = await apiJson<Status>('/api/settings/model-access', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode, ...extra }),
      })
      setStatus(next)
      setConfirming(false)
      setReason('')
      if (next.unchanged) {
        setNotice(`Already set to ${mode}; nothing was written.`)
      } else {
        setNotice(
          `Written: ${mode}. The gateway reads it within about a minute, but nothing on this page can confirm it has yet. ` +
            `To confirm, wait a couple of minutes and use "Check the refusal log": a refusal line naming this write ` +
            `(mode_source=override, mode_set_at ${next.override.state === 'set' ? next.override.setAt : ''}) is proof the gateway read it. ` +
            `A gateway that cannot read the parameter fails quietly, so an unconfirmed result is not success.`,
        )
      }
      if (next.auditError) {
        setActionError(
          `The change landed but its audit row did NOT: ${next.auditError}. Record it by hand: who, when, ${mode}.`,
        )
      }
      if (viewing) void loadImpact()
    } catch (err) {
      setActionError(errorMessage(err))
      // The write may or may not have landed: re-read the live parameter
      // rather than leaving the page showing what it last believed.
      void loadStatus()
    } finally {
      setBusy(false)
    }
  }

  function confirmEnforce() {
    // What the operator was SHOWN, sent so the audit row records it. A claim
    // from the browser, labelled as one by the server.
    const impactShown = impact
      ? {
          notEntitledPeople: impact.impact.notEntitled.people,
          checkFailedPeople: impact.impact.checkFailed.people,
          modelsWithNoGrant: impact.impact.modelsWithNoGrant.map((m) => m.model),
          complete: impact.impact.complete,
          windowHours: impact.impact.windowHours,
          readAt: impact.impact.until,
        }
      : { readError: impactError ?? 'not read' }
    void put('enforce', { confirm: true, reason: reason.trim(), impactShown })
  }

  const pill = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.25rem 0.7rem',
    borderRadius: '9999px',
    border: `1px solid ${tokens.border}`,
    fontSize: '0.82rem',
    background: tokens.surfaceRaised,
    color: tokens.textPrimary,
  } as const
  const effective = status?.effective ?? null
  const pillLabel =
    status === null
      ? '…'
      : effective === 'enforce'
        ? 'Enforcing'
        : effective === 'observe'
          ? 'Observing'
          : effective === 'off'
            ? 'Guardrail off in terraform'
            : 'Mode unknown'
  const enforcing = effective === 'enforce'
  const box = {
    marginTop: '1rem',
    padding: '1rem 1.2rem',
    borderRadius: '0.8rem',
    background: tokens.surfaceRaised,
  } as const

  function renderFacts(s: Status) {
    const o = s.override
    const configured =
      s.configured === null
        ? 'The mode terraform configured is not known to this console (MODEL_ACCESS_CONFIGURED_MODE was not set on this task).'
        : `Terraform configured: ${s.configured}.`
    if (s.effectiveBecause === 'guardrail-off') {
      return <p style={styles.hint}>{s.cannotSetReason}</p>
    }
    if (o.state === 'set') {
      return (
        <p style={styles.hint}>
          Override is set to <strong>{o.mode}</strong>
          {o.setBy ? ` by ${o.setBy}` : ''}
          {o.setAt ? ` on ${when(o.setAt)}` : ''}. {configured}
        </p>
      )
    }
    if (o.state === 'error') {
      return (
        <p style={styles.error}>
          Could not read the live parameter ({o.error}). This page does not know what the gateway is using, and will not
          guess: if the gateway cannot read it either, it keeps the last override it saw, or the configured mode if it
          never saw one. {configured}
        </p>
      )
    }
    const fallback =
      s.configured === null
        ? 'The configured mode it falls back to is unknown to this console.'
        : `The gateway is using the mode terraform configured: ${s.configured}.`
    if (o.state === 'ignored') {
      return (
        <p style={styles.hint}>
          A value is present at {s.parameter} but the gateway will ignore it: {o.problem}. {fallback}
        </p>
      )
    }
    return (
      <p style={styles.hint}>
        No override is set, so nobody has chosen this from the console. {fallback}
      </p>
    )
  }

  function renderImpact(r: ImpactResponse) {
    const i = r.impact
    const people = i.notEntitled.people
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        {!i.complete ? (
          <p style={{ ...styles.error, margin: 0 }}>
            The scan stopped early ({i.incompleteReason}). It reads oldest first, so the most recent part of the last{' '}
            {i.windowHours} hours was NOT read and every figure below is a minimum.
          </p>
        ) : null}

        {people > 0 ? (
          <div>
            <p style={{ margin: 0, color: tokens.textPrimary, fontWeight: 600 }}>
              {plural(people, 'person', 'people')} would have been refused in the last {i.windowHours} hours.
            </p>
            <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.2rem', color: tokens.textSecondary }}>
              {i.notEntitled.byModel.map((m) => (
                <li key={m.model}>
                  <code>{m.model}</code>: {plural(m.people, 'person', 'people')} ({plural(m.requests, 'request', 'requests')})
                </li>
              ))}
            </ul>
            <p style={styles.hint}>
              {i.notEntitled.peopleList.slice(0, SHOWN_PEOPLE).join(', ')}
              {people > SHOWN_PEOPLE ? ` and ${people - SHOWN_PEOPLE} more` : ''}
            </p>
          </div>
        ) : (
          <div>
            <p style={{ margin: 0, color: tokens.textPrimary, fontWeight: 600 }}>
              The log shows nobody refused for lacking a model grant in the last {i.windowHours} hours.
            </p>
            <p style={{ ...styles.hint, margin: '0.3rem 0 0' }}>
              This is weak evidence, not a clean bill of health. The guardrail logs refusals only; allowed requests are
              logged at debug level and are not in this log. An empty result means either nothing would be refused or
              nothing was looked at, and this page cannot tell which.
            </p>
          </div>
        )}

        {i.modelsWithNoGrant.length > 0 ? (
          <p style={{ ...styles.error, margin: 0 }}>
            No usable grant on: {i.modelsWithNoGrant.map((m) => `${m.model} (${plural(m.requests, 'request', 'requests')})`).join(', ')}.
            Enforcement would refuse EVERYONE on {i.modelsWithNoGrant.length === 1 ? 'this model' : 'these models'}. Give{' '}
            {i.modelsWithNoGrant.length === 1 ? 'it' : 'them'} a grant on the Models page first.
          </p>
        ) : null}

        {i.checkFailed.requests > 0 ? (
          <p style={{ ...styles.hint, margin: 0 }}>
            {plural(i.checkFailed.people, 'person', 'people')} had {plural(i.checkFailed.requests, 'request', 'requests')} that would be
            refused because the access check itself failed (Graph error or timeout), not because of their grant. Under
            enforcement those requests are refused too. They are not counted above.
          </p>
        ) : null}
        {i.noIdentity.requests > 0 ? (
          <p style={{ ...styles.hint, margin: 0 }}>
            {plural(i.noIdentity.requests, 'request', 'requests')} carried no identity and would be refused. Not counted as a person.
          </p>
        ) : null}
        {i.otherRefusals.requests > 0 ? (
          <p style={{ ...styles.hint, margin: 0 }}>
            {plural(i.otherRefusals.requests, 'request', 'requests')} named a model this gateway does not recognise.
          </p>
        ) : null}
        {i.modesSeen.length > 1 ? (
          <p style={{ ...styles.hint, margin: 0 }}>
            The gateway changed mode inside this window ({i.modesSeen.join(', ')}). People it was already refusing while
            enforcing are counted here too.
          </p>
        ) : null}

        <p style={{ ...styles.hint, margin: 0 }}>
          Counts only people who tried a model they lack during these {i.windowHours} hours. Read {when(i.until)}; newest
          refusal line:{' '}
          {i.lastLogged ? `mode=${i.lastLogged.mode} at ${when(i.lastLogged.at)}` : 'none in the window'}.
        </p>

        {(i.overrideWarnings ?? []).map((w) => (
          <p key={w.kind} style={{ ...(w.kind === 'unreadable' || w.kind === 'gate-misconfigured' ? styles.error : styles.hint), margin: 0 }}>
            {w.kind === 'unreadable'
              ? `The gateway logged ${plural(w.count, 'warning', 'warnings')} that it could NOT READ the override parameter (newest ${when(w.newestAt)}). Check the gateway task role can read it.`
              : w.kind === 'not-used'
                ? `The gateway read a value at the parameter that it will not use (${plural(w.count, 'warning', 'warnings')}, newest ${when(w.newestAt)}): it is falling back to the configured mode.`
                : `The gateway is ignoring the override because its own model gate is misconfigured (newest ${when(w.newestAt)}).`}{' '}
            <code>{w.text.slice(0, 160)}</code>
          </p>
        ))}

        {r.confirmation === 'gateway-cannot-read' ? (
          <p style={{ ...styles.error, margin: 0 }}>
            The gateway is NOT seeing this write: it is deciding on an override it could not re-read, or has logged that it cannot
            read the parameter since the flip. What this console shows is not what the gateway is doing.
          </p>
        ) : null}
        {r.confirmation === 'confirmed' ? (
          <p style={{ ...styles.hint, margin: 0, color: tokens.brand }}>
            The gateway has logged mode={status?.override.state === 'set' ? status.override.mode : '?'} since the override
            was set: it is acting on it.
          </p>
        ) : null}
        {r.confirmation === 'contradicted' ? (
          <p style={{ ...styles.error, margin: 0 }}>
            The gateway last logged mode={i.lastLogged?.mode} well AFTER this was set. It is not acting on the override.
            The likeliest cause is a gateway image that does not read the parameter yet.
          </p>
        ) : null}
        {r.confirmation === 'unconfirmed' ? (
          <p style={{ ...styles.hint, margin: 0 }}>
            Not confirmed: no refusal has been logged since the override was set. On a quiet platform that is normal, but
            it does not show the gateway is acting on it.
          </p>
        ) : null}
      </div>
    )
  }

  function renderImpactBlock() {
    if (impactLoading) return <p style={styles.hint}>Reading the last 24 hours of the gateway&rsquo;s refusal log…</p>
    if (impactError) {
      return (
        <p style={styles.error}>
          Could not read the refusal log: {impactError}. That is not the same as nobody being affected: the impact is
          unknown.
        </p>
      )
    }
    return impact ? renderImpact(impact) : null
  }

  return (
    <section style={styles.section} aria-label="Model access mode">
      <div style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle}>Model access mode</h2>
        <span style={{ ...pill, ...(enforcing ? { background: tokens.brand, color: '#FFFFFF' } : {}) }}>{pillLabel}</span>
      </div>
      <p style={styles.sectionSubtitle}>
        Whether the gateway actually refuses a model a person has not been granted. <strong>Observe</strong> evaluates
        every request and logs what it would refuse, and refuses nothing. <strong>Enforce</strong> refuses. The gateway
        reads this within about a minute; no deploy. Taking the guardrail out of the request path entirely (off) stays a
        terraform change.
      </p>

      {statusError ? <p style={styles.error}>{statusError}</p> : null}
      {status ? renderFacts(status) : null}
      {notice ? <p style={{ ...styles.hint, color: tokens.brand }}>{notice}</p> : null}
      {actionError ? <p style={styles.error}>{actionError}</p> : null}

      {status && status.canSet ? (
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          {effective !== 'enforce' ? (
            <button type="button" style={styles.button} onClick={openConfirm} disabled={busy}>
              Turn on enforcement
            </button>
          ) : null}
          {effective !== 'observe' ? (
            // The safe direction: no confirm, on purpose. A control people are
            // afraid to turn off is one they avoid turning on.
            <button type="button" style={styles.buttonGhost} onClick={() => void put('observe')} disabled={busy}>
              Back to observe
            </button>
          ) : null}
          <button type="button" style={styles.buttonGhost} onClick={openViewer} disabled={busy || impactLoading}>
            Check the refusal log
          </button>
        </div>
      ) : null}

      {viewing && !confirming ? <div style={{ ...box, border: `1px solid ${tokens.border}` }}>{renderImpactBlock()}</div> : null}

      {confirming ? (
        <div role="alertdialog" aria-label="Confirm turning on model access enforcement" style={{ ...box, border: `1px solid ${tokens.danger}` }}>
          <p style={{ margin: '0 0 0.6rem', color: tokens.textPrimary, fontWeight: 600 }}>
            Turning this on starts refusing requests for models a person has not been granted, within about a minute.
          </p>
          {renderImpactBlock()}
          <div style={styles.field}>
            <label style={styles.fieldLabel} htmlFor="model-access-reason">
              Reason (recorded in the audit log)
            </label>
            <input
              id="model-access-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              style={styles.input}
              disabled={busy}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.6rem' }}>
            <button
              type="button"
              style={styles.button}
              onClick={confirmEnforce}
              disabled={busy || impactLoading || reason.trim().length < 3}
            >
              {busy
                ? 'Working…'
                : impactError
                  ? 'Turn on enforcement without the impact'
                  : 'Yes, turn on enforcement'}
            </button>
            <button type="button" style={styles.buttonGhost} onClick={() => setConfirming(false)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </section>
  )
}

export default ModelAccessSwitch
